import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiBaseOrigin } from '../../../../shared/config/api-origin';
import { PlatformHeader } from '../../../../shared/platform/PlatformHeader';
import '../../../overview/ui/overview.css';
import {
  createExternalAdapter,
  externalLedgerQuery,
  externalQuery,
  ExternalRequestError,
} from '../../adapters/historical/external-http';
import { HISTORICAL_METHODS, historicalHref } from '../../consumer/historical/contract';
import type {
  ExternalDay,
  ExternalFilter,
  ExternalGroup,
  ExternalPageResult,
  ExternalPort,
  ExternalRow,
  ExternalRun,
} from '../../consumer/historical/external-contract';
import {
  EXTERNAL_ROOT,
  EXTERNAL_TRACKS,
  externalHref,
  externalTrackLabel,
  INITIAL_EXTERNAL_FILTER,
  parseExternalLocation,
} from '../../consumer/historical/external-contract';
import '../management/management.css';
import '../normal-monitoring.css';
import './external.css';
import './historical.css';
const numeric = (v: number | null) => (v === null ? '不可评估' : String(v));
const compact = (v: number | null) => (v === null ? '不可评估' : v.toFixed(3));
function useRead<T>(read: (signal: AbortSignal) => Promise<T>) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    status: 'LOADING' | 'READY' | 'ERROR';
    data: T | null;
    error: unknown;
  }>({ status: 'LOADING', data: null, error: null });
  useEffect(() => {
    const c = new AbortController();
    let active = true;
    setState({ status: 'LOADING', data: null, error: null });
    void read(c.signal).then(
      (data) => {
        if (active) setState({ status: 'READY', data, error: null });
      },
      (error) => {
        if (active) setState({ status: 'ERROR', data: null, error });
      },
    );
    return () => {
      active = false;
      c.abort();
    };
  }, [read, attempt]);
  return { ...state, retry: () => setAttempt((a) => a + 1) };
}
export function ExternalReadNotice({
  status,
  error,
  retry,
}: {
  status: string;
  error: unknown;
  retry: () => void;
}) {
  if (status === 'READY') return null;
  return (
    <div role="status" className="nm-external-notice">
      <p>
        {status === 'LOADING'
          ? '正在读取已保存的跨地区验证…'
          : error instanceof ExternalRequestError
            ? `${error.status} · ${error.code} · 外部验证读取失败，结果未知。`
            : '服务离线或响应不符合契约。结果未知；未使用演示数值替代。'}
      </p>
      {status === 'ERROR' && (
        <button type="button" onClick={retry}>
          重试外部验证
        </button>
      )}
    </div>
  );
}
function Pager({
  offset,
  limit,
  total,
  onChange,
  label,
}: {
  offset: number;
  limit: number;
  total: number;
  onChange: (n: number) => void;
  label: string;
}) {
  return (
    <nav className="nm-pagination" aria-label={label}>
      <button
        type="button"
        disabled={!offset}
        onClick={() => onChange(Math.max(0, offset - limit))}
      >
        上一页
      </button>
      <span>
        第 {Math.floor(offset / limit) + 1} 页 · 共 {total} 条
      </span>
      <button
        type="button"
        disabled={offset + limit >= total || offset + limit > 2000000}
        onClick={() => onChange(offset + limit)}
      >
        下一页
      </button>
    </nav>
  );
}
export function ExternalKPIs({ run }: { run: ExternalRun }) {
  return (
    <div className="nm-external-kpis">
      <div>
        <span>研究气候背景</span>
        <strong>{new Set(run.cohort.map((c) => c.climateContext)).size}</strong>
      </div>
      <div>
        <span>独立测试年度</span>
        <strong>2024 / 2025</strong>
      </div>
      <div>
        <span>已保存分组</span>
        <strong>{run.groupCount}</strong>
      </div>
      <div>
        <span>当地验证 / 原模型迁移</span>
        <strong>
          {run.externalValidationUtility} / {run.transferStability}
        </strong>
      </div>
    </div>
  );
}
export function ExternalProvenance({ run }: { run: ExternalRun }) {
  return (
    <details className="nm-management-card">
      <summary>数据来源、冻结参数与可追溯证据</summary>
      <p>
        NOAA GHCNh · {run.sourceType} · {run.license} · DOI {run.doi}
      </p>
      <p>研究气候背景（STUDY_CLIMATE_CONTEXT），并非官方 Köppen 分类。</p>
      {run.cohort.map((c) => (
        <p key={c.stationId}>
          {c.name} · {c.stationId} · {c.country} · {c.climateContext} · {c.latitude}, {c.longitude}
          <br />
          {c.climateEvidence}
        </p>
      ))}
      <div className="nm-external-hashes">
        {(
          [
            'frozenInputSHA',
            'preregistrationSHA',
            'preregistrationHash',
            'algorithmSHA',
            'localFreezeSHA',
            'donorFreezeSHA',
            'sourceDatasetRevision',
            'snapshotHash',
            'rowsHash',
            'groupsHash',
            'daysHash',
          ] as const
        ).map((k) => (
          <p key={k}>
            {k}: {run[k]}
          </p>
        ))}
      </div>
      <p>
        2022 TRAIN {run.bounds.trainStart} → {run.bounds.trainEnd}
        <br />
        2023 VALIDATION {run.bounds.validationStart} → {run.bounds.validationEnd}
        <br />
        2024 HELD_OUT {run.bounds.heldout2024Start} → {run.bounds.heldout2024End}
        <br />
        2025 HELD_OUT {run.bounds.heldout2025Start} → {run.bounds.heldout2025End}
        <br />
        embargo {run.bounds.embargoHours}h
      </p>
      {run.sources.map((s) => (
        <details key={`${s.stationId}-${s.year}`}>
          <summary>
            {s.stationId} / {s.year} · 温度 {compact(s.temperatureCoverage)} / 风速{' '}
            {compact(s.windCoverage)} 覆盖
          </summary>
          <p>
            {s.url.startsWith('https://www.ncei.noaa.gov/') ? (
              <a href={s.url} target="_blank" rel="noreferrer">
                官方原始数据
              </a>
            ) : (
              s.url
            )}
          </p>
          <p>
            originalHash {s.originalHash}
            <br />
            snapshotHash {s.snapshotHash}
            <br />
            {s.bytes} bytes · {s.rawRows} 原始行 · 温度 {s.temperatureHours} 小时 / 风速{' '}
            {s.windHours} 小时 · 访问 {s.accessedAt}
          </p>
          <pre>{s.qualityJson}</pre>
        </details>
      ))}
      <details>
        <summary>冻结参数原文</summary>
        <pre>{run.parametersJson}</pre>
      </details>
      {run.limitations.map((l, i) => (
        <p key={i}>{l}</p>
      ))}
    </details>
  );
}
export function ExternalComparisonChart({ groups }: { groups: ExternalGroup[] }) {
  const max = Math.max(0.001, ...groups.flatMap((g) => [g.pairedMAE ?? 0, g.baselineMAE ?? 0]));
  return (
    <div
      className="nm-external-chart-scroll"
      role="region"
      aria-label="当前分页 paired MAE 对比，可横向滚动"
      tabIndex={0}
    >
      <svg
        className="nm-external-chart"
        width="860"
        height={Math.max(110, groups.length * 72 + 65)}
        role="img"
        aria-label="候选与 LAST_VALUE 相同起点的 MAE；越小越好"
      >
        <text x="16" y="23">
          当前筛选与分页 · 单位 {groups[0]?.unit ?? '未知'} · 蓝色候选 / 灰色 LAST_VALUE · 越小越好
        </text>
        {groups.map((g, i) => {
          const y = 50 + i * 72;
          return (
            <g key={g.groupId}>
              <text x="16" y={y}>
                {g.stationId} · {g.testYear} · +{g.horizonHours}h
              </text>
              <text x="16" y={y + 20}>
                {g.method.replace('_HOURLY_V1', '')} {g.donorStationId ?? ''}
              </text>
              <rect
                x="310"
                y={y - 10}
                width={((g.pairedMAE ?? 0) / max) * 400}
                height="16"
                fill="#2563eb"
              />
              <text x="720" y={y + 3}>
                {compact(g.pairedMAE)}
              </text>
              <rect
                x="310"
                y={y + 12}
                width={((g.baselineMAE ?? 0) / max) * 400}
                height="16"
                fill="#64748b"
              />
              <text x="720" y={y + 25}>
                {compact(g.baselineMAE)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
export function ExternalGroupCards({
  data,
  runId,
  filter,
  areaId,
}: {
  data: ExternalPageResult<ExternalGroup>;
  runId: string;
  filter: ExternalFilter;
  areaId: string;
}) {
  return (
    <>
      <p>
        匹配 {data.total} 组 · 当前 {data.items.length} 组（每页12组）。Δ = 候选 −
        LAST_VALUE：负数改善，正数退化。各年度分别保存；不可评估没有替换为零。
      </p>
      {!data.items.length ? (
        <p className="nm-empty">当前筛选无外部验证分组。</p>
      ) : (
        <>
          <ExternalComparisonChart groups={data.items} />
          <div className="nm-external-groups">
            {data.items.map((g) => (
              <article key={g.groupId} data-external-group-id={g.groupId}>
                <h3>
                  {g.stationId} · {g.testYear} · {g.metric} +{g.horizonHours}h
                </h3>
                <p>
                  {g.country} · {g.climateContext}
                  <br />
                  {g.method} · {g.donorStationId ?? '当地模型'}
                </p>
                <p>
                  paired MAE {compact(g.pairedMAE)} / RMSE {compact(g.pairedRMSE)} {g.unit}
                  <br />
                  LAST_VALUE {compact(g.baselineMAE)} / {compact(g.baselineRMSE)}
                  <br />
                  ΔMAE {compact(g.baselineMAEDelta)} · ΔRMSE {compact(g.baselineRMSEDelta)}
                  <br />
                  paired n {g.pairedCount} / origins {g.totalOrigins} · own coverage{' '}
                  {compact(g.coverage)}
                </p>
                <p>
                  {g.eligibility} · {g.utility}
                </p>
                <a href={externalHref(runId, filter, areaId, g.groupId)}>查看分组详情与错误案例</a>
              </article>
            ))}
          </div>
        </>
      )}
    </>
  );
}
function Filters({
  run,
  filter,
  apply,
}: {
  run: ExternalRun;
  filter: ExternalFilter;
  apply: (f: ExternalFilter) => void;
}) {
  const [draft, setDraft] = useState(filter);
  useEffect(() => setDraft(filter), [filter]);
  const [error, setError] = useState('');
  const select = (
    key: keyof ExternalFilter,
    label: string,
    options: { value: string; label: string }[],
  ) => (
    <label key={key}>
      {label}
      <select
        aria-label={label}
        value={String(draft[key])}
        onChange={(e) => {
          const value = e.target.value;
          setDraft((d) =>
            key === 'track'
              ? {
                  ...d,
                  track: value,
                  testYear: value === EXTERNAL_TRACKS[1] ? '2025' : '2024',
                  donorStationId: value === EXTERNAL_TRACKS[1] ? 'AEI0000OMAA' : '',
                }
              : key === 'country' || key === 'climateContext'
                ? { ...d, [key]: value, stationId: '' }
                : { ...d, [key]: value },
          );
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
  const opts = (values: string[]) => [
    { value: '', label: '全部' },
    ...values.map((value) => ({ value, label: value })),
  ];
  const stations = run.cohort.filter(
    (c) =>
      (!draft.country || c.country === draft.country) &&
      (!draft.climateContext || c.climateContext === draft.climateContext),
  );
  return (
    <section className="nm-management-card">
      <h2>实验轨道与范围</h2>
      <form
        className="nm-filters"
        onSubmit={(e) => {
          e.preventDefault();
          try {
            externalQuery(draft);
            apply({ ...draft, offset: 0 });
            setError('');
          } catch {
            setError('筛选无效：零样本迁移仅评价2025。');
          }
        }}
      >
        {select(
          'track',
          '实验轨道',
          EXTERNAL_TRACKS.map((value) => ({ value, label: externalTrackLabel(value) })),
        )}
        {select(
          'climateContext',
          '气候背景',
          opts([...new Set(run.cohort.map((c) => c.climateContext))]),
        )}
        {select('country', '国家', opts([...new Set(run.cohort.map((c) => c.country))]))}
        {select('stationId', '站点', [
          { value: '', label: '全部站点' },
          ...stations.map((c) => ({ value: c.stationId, label: `${c.name} / ${c.stationId}` })),
        ])}
        {select(
          'testYear',
          '独立测试年度',
          (draft.track === EXTERNAL_TRACKS[1] ? ['2025'] : ['2024', '2025']).map((value) => ({
            value,
            label: value,
          })),
        )}
        {select('metric', '气象指标', [
          { value: 'TEMPERATURE', label: '温度 / degC' },
          { value: 'WIND_SPEED', label: '风速 / m/s' },
        ])}
        {select(
          'horizonHours',
          '预测时长',
          ['1', '3', '6'].map((value) => ({ value, label: `+${value}h` })),
        )}
        {select('method', '比较方法', opts([...HISTORICAL_METHODS]))}
        {draft.track === EXTERNAL_TRACKS[1] &&
          select('donorStationId', '原 UAE donor', [
            { value: 'AEI0000OMAA', label: 'OMAA · 预注册主 donor' },
            { value: 'AEI0000OMDB', label: 'OMDB · 次 donor，独立报告' },
            { value: '', label: '两个 donor 并列' },
          ])}
        <button type="submit">应用外部验证筛选</button>
      </form>
      {error && <p role="alert">{error}</p>}
      <p>
        {draft.track === EXTERNAL_TRACKS[0]
          ? '2022当地训练，2023仅选 alpha；同一冻结参数分别评价2024、2025。'
          : '原 VS05 UAE 2024 means / scales / coefficients 原样应用于外部站点2025；不在目标站重拟合，不事后挑最佳 donor。'}
      </p>
    </section>
  );
}
export function ExternalSummaries({ run, filter }: { run: ExternalRun; filter: ExternalFilter }) {
  const summaries = run.summaries.filter(
    (s) =>
      s.track === filter.track &&
      s.testYear === Number(filter.testYear) &&
      s.metric === filter.metric &&
      (!filter.method || s.method === filter.method) &&
      (!filter.donorStationId || s.donorStationId === filter.donorStationId) &&
      (!filter.climateContext || s.climateContext === filter.climateContext),
  );
  return (
    <details className="nm-management-card">
      <summary>已保存的年度稳定性统计 · macro / micro</summary>
      <p>
        汇总范围为预注册全部站点及 +1 / +3 / +6h；按轨道、气候背景、年度、指标、方法与 donor
        选择。国家、站点与单一时长筛选仅作用于下方分组，不改变这些不可变汇总。micro 按 paired
        起点数合并误差；macro 对站点 / 时长组等权。不同单位不混合。
      </p>
      {summaries.map((s, i) => (
        <p key={i}>
          {s.climateContext ?? '全部气候背景'} · {s.testYear} · {s.metric} · {s.method} · donor{' '}
          {s.donorStationId ?? '无'}
          <br />
          macro {s.macroGroups}组 MAE {numeric(s.macroMAE)} / RMSE {numeric(s.macroRMSE)} ·
          LAST_VALUE {numeric(s.macroBaselineMAE)} / {numeric(s.macroBaselineRMSE)}
          <br />
          micro paired n {s.pairedCount} · MAE {numeric(s.microMAE)} / RMSE {numeric(s.microRMSE)} /
          Bias {numeric(s.microBias)} · LAST_VALUE {numeric(s.microBaselineMAE)} /{' '}
          {numeric(s.microBaselineRMSE)}
        </p>
      ))}
      {!summaries.length && <p>当前范围没有已保存汇总；不计算或补造结果。</p>}
    </details>
  );
}
function GroupsRead({
  port,
  id,
  filter,
  areaId,
  apply,
}: {
  port: ExternalPort;
  id: string;
  filter: ExternalFilter;
  areaId: string;
  apply: (f: ExternalFilter) => void;
}) {
  const read = useCallback(
    (signal: AbortSignal) => port.groups(id, filter, signal),
    [port, id, filter],
  );
  const s = useRead(read);
  return (
    <section className="nm-management-card">
      <h2>跨气候、跨年度对比</h2>
      <ExternalReadNotice {...s} />
      {s.status === 'READY' && s.data && (
        <>
          <ExternalGroupCards data={s.data} runId={id} filter={filter} areaId={areaId} />
          <Pager
            offset={filter.offset}
            limit={filter.limit}
            total={s.data.total}
            label="外部分组分页"
            onChange={(offset) => apply({ ...filter, offset })}
          />
        </>
      )}
    </section>
  );
}
export function ExternalGroupEvidence({ group: g }: { group: ExternalGroup }) {
  return (
    <section className="nm-management-card">
      <h2 tabIndex={-1} id="external-group-heading">
        {g.stationId} · {g.testYear} · {g.metric} +{g.horizonHours}h
      </h2>
      <p>
        {externalTrackLabel(g.track)} · {g.method} · donor {g.donorStationId ?? '无'}
        <br />
        {g.climateContext} / {g.country} · {g.eligibility} · {g.utility}
      </p>
      <dl className="nm-external-metrics">
        <dt>paired MAE / RMSE / Bias</dt>
        <dd>
          {numeric(g.pairedMAE)} / {numeric(g.pairedRMSE)} / {numeric(g.pairedBias)} {g.unit}
        </dd>
        <dt>相同起点 LAST_VALUE</dt>
        <dd>
          {numeric(g.baselineMAE)} / {numeric(g.baselineRMSE)} / {numeric(g.baselineBias)} {g.unit}
        </dd>
        <dt>候选 − 基准</dt>
        <dd>
          MAE {numeric(g.baselineMAEDelta)} / RMSE {numeric(g.baselineRMSEDelta)}
        </dd>
        <dt>自身覆盖 / paired n</dt>
        <dd>
          {numeric(g.coverage)} / {g.pairedCount}
        </dd>
      </dl>
      <details>
        <summary>完整分母、缺失与冻结模型</summary>
        <p>
          origins {g.totalOrigins} · generated {g.generated} · evaluable {g.evaluable} · paired{' '}
          {g.pairedCount}
        </p>
        <p>
          own MAE {numeric(g.mae)} / RMSE {numeric(g.rmse)} / Bias {numeric(g.bias)}
        </p>
        <p>modelFreezeSHA {g.modelFreezeSHA}</p>
        {g.unavailableReasons.map((r) => (
          <p key={r.reason}>
            {r.reason}: {r.count}
          </p>
        ))}
      </details>
      <details>
        <summary>95% 连续日期块区间与月份失败分析</summary>
        <p>
          ΔMAE [{numeric(g.interval.maeLow)}, {numeric(g.interval.maeHigh)}] · ΔRMSE [
          {numeric(g.interval.rmseLow)}, {numeric(g.interval.rmseHigh)}]
        </p>
        <p>
          {g.interval.status} · {g.interval.blockDays}日连续块 · {g.interval.replicates}次重采样 ·
          seed {g.interval.seed} · 有效日 {g.interval.effectiveDays} · 可用块{' '}
          {g.interval.possibleBlocks}
        </p>
        <p>
          条件性研究不确定区间；重叠小时样本存在相关性，origin
          数不等于独立样本数。这不是消防认证或实时提前发布证明。
        </p>
        {g.months.map((m) => (
          <p key={m.month}>
            {m.month} · n {m.n} · MAE {numeric(m.mae)} / RMSE {numeric(m.rmse)} / Bias{' '}
            {numeric(m.bias)} · LAST_VALUE {numeric(m.baselineMAE)} / {numeric(m.baselineRMSE)}
          </p>
        ))}
      </details>
    </section>
  );
}
export function ExternalRows({ rows }: { rows: ExternalRow[] }) {
  return (
    <>
      {rows.map((r, i) => (
        <details key={`${r.origin}-${i}`} data-external-origin={r.origin}>
          <summary>
            {r.origin} → {r.targetAt} · {r.status} · 预测 {compact(r.predicted)} / 实际{' '}
            {compact(r.actual)} {r.unit}
          </summary>
          <p>
            基准 {numeric(r.baselinePredicted)} · paired {String(r.paired)} ·{' '}
            {r.reason ?? '无已记录不可用原因'}
            <br />
            input {r.inputStart ?? '未知'} → {r.inputEnd ?? '未知'}
          </p>
          <p>
            modelFreezeSHA {r.modelFreezeSHA}
            <br />
            sourceDatasetRevision {r.sourceDatasetRevision}
          </p>
          <details>
            <summary>原始观测引用（hash:line:metric）</summary>
            <p>inputRefs（仅截至 origin）</p>
            <pre>{r.inputRefs.join('\n')}</pre>
            <p>targetRefs（评价标签，非模型输入）</p>
            <pre>{r.targetRefs.join('\n')}</pre>
          </details>
        </details>
      ))}
      {!rows.length && <p className="nm-empty">当前分页没有样本；不能据此声称没有误差。</p>}
    </>
  );
}
function Days({ days }: { days: ExternalDay[] }) {
  return (
    <div className="nm-table-wrap" role="region" tabIndex={0} aria-label="日级误差台账，可横向滚动">
      <table className="nm-table">
        <caption>UTC 日期 · 保存的误差总和，用于连续日期块统计</caption>
        <thead>
          <tr>
            <th>日期 / n</th>
            <th>候选绝对 / 平方 / 有符号总和</th>
            <th>基准绝对 / 平方 / 有符号总和</th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.day}>
              <td>
                {d.day} / {d.n}
              </td>
              <td>
                {d.absoluteErrorSum} / {d.squaredErrorSum} / {d.signedErrorSum}
              </td>
              <td>
                {d.baselineAbsoluteErrorSum} / {d.baselineSquaredErrorSum} /{' '}
                {d.baselineSignedErrorSum}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!days.length && <p>当前日期范围没有日级样本。</p>}
    </div>
  );
}
function Ledger({
  port,
  id,
  groupId,
  mode,
}: {
  port: ExternalPort;
  id: string;
  groupId: string;
  mode: 'rows' | 'errors' | 'days';
}) {
  const [offset, setOffset] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  const [error, setError] = useState('');
  const f = useMemo(() => ({ groupId, ...range, offset, limit: 12 }), [groupId, range, offset]);
  const read = useCallback(
    async (
      signal: AbortSignal,
    ): Promise<ExternalPageResult<ExternalRow> | ExternalPageResult<ExternalDay>> =>
      mode === 'days'
        ? port.days(id, f, signal)
        : mode === 'errors'
          ? port.errors(id, f, signal)
          : port.rows(id, f, signal),
    [port, id, f, mode],
  );
  const s = useRead(read);
  return (
    <section className="nm-management-card">
      <h3>
        {mode === 'errors'
          ? '真实错误案例'
          : mode === 'days'
            ? '日级误差台账'
            : '全部起点与缺失样本'}
      </h3>
      {mode !== 'errors' && (
        <form
          className="nm-filters"
          onSubmit={(e) => {
            e.preventDefault();
            try {
              externalLedgerQuery({ ...f, from, to });
              setRange({ from, to });
              setOffset(0);
              setError('');
            } catch {
              setError('日期需合法 UTC 时间，开始含、结束不含，开始早于结束。');
            }
          }}
        >
          <label>
            目标开始 UTC（含）
            <input
              value={from}
              placeholder="2025-01-02T00:00:00Z"
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label>
            目标结束 UTC（不含）
            <input
              value={to}
              placeholder="2026-01-01T00:00:00Z"
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <button type="submit">应用日期范围</button>
        </form>
      )}
      {error && <p role="alert">{error}</p>}
      <ExternalReadNotice {...s} />
      {s.status === 'READY' && s.data && (
        <>
          {mode === 'days' ? (
            <Days days={s.data.items as ExternalDay[]} />
          ) : (
            <ExternalRows rows={s.data.items as ExternalRow[]} />
          )}
          <Pager
            offset={offset}
            limit={12}
            total={s.data.total}
            label={`${mode}分页`}
            onChange={setOffset}
          />
        </>
      )}
    </section>
  );
}
function GroupRead({ port, id, groupId }: { port: ExternalPort; id: string; groupId: string }) {
  const read = useCallback(
    (signal: AbortSignal) => port.group(id, groupId, signal),
    [port, id, groupId],
  );
  const s = useRead(read);
  const [tab, setTab] = useState<'errors' | 'rows' | 'days'>('errors');
  return (
    <>
      <ExternalReadNotice {...s} />
      {s.status === 'READY' && s.data && (
        <>
          <ExternalGroupEvidence group={s.data} />
          <div className="nm-external-tabs" role="group" aria-label="分组证据类型">
            {(['errors', 'rows', 'days'] as const).map((t) => (
              <button type="button" key={t} aria-pressed={tab === t} onClick={() => setTab(t)}>
                {t === 'errors' ? '错误案例' : t === 'rows' ? '全部与缺失样本' : '日级误差'}
              </button>
            ))}
          </div>
          <Ledger key={`${groupId}-${tab}`} port={port} id={id} groupId={groupId} mode={tab} />
        </>
      )}
    </>
  );
}
function RunRead({
  port,
  id,
  groupId,
  filter,
  areaId,
  apply,
}: {
  port: ExternalPort;
  id: string;
  groupId: string | null;
  filter: ExternalFilter;
  areaId: string;
  apply: (f: ExternalFilter) => void;
}) {
  const read = useCallback((signal: AbortSignal) => port.run(id, signal), [port, id]);
  const s = useRead(read);
  return (
    <>
      <ExternalReadNotice {...s} />
      {s.status === 'READY' && s.data && (
        <>
          <p className="nm-provenance" data-external-run-id={s.data.runId}>
            runId {s.data.runId} · 工程运行状态 {s.data.status}
          </p>
          <ExternalKPIs run={s.data} />
          {groupId ? (
            <>
              <a href={externalHref(id, filter, areaId)}>← 返回当前分组比较</a>
              <GroupRead port={port} id={id} groupId={groupId} />
            </>
          ) : (
            <>
              <Filters run={s.data} filter={filter} apply={apply} />
              <ExternalSummaries run={s.data} filter={filter} />
              <GroupsRead port={port} id={id} filter={filter} areaId={areaId} apply={apply} />
            </>
          )}
          <ExternalProvenance run={s.data} />
        </>
      )}
    </>
  );
}
export function ExternalRunList({
  data,
  areaId,
}: {
  data: ExternalPageResult<ExternalRun>;
  areaId: string;
}) {
  return (
    <>
      {!data.items.length && (
        <p className="nm-empty">尚无已保存的跨地区验证实验。未获取或生成示例结果。</p>
      )}
      {data.items.map((r) => (
        <article className="nm-management-card" key={r.runId}>
          <h2>跨地区稳定性验证</h2>
          <p>
            {r.createdAt} · {r.status}
            <br />
            当地验证 {r.externalValidationUtility} · 原模型迁移 {r.transferStability}
            <br />
            {r.cohort.map((c) => `${c.stationId} / ${c.country}`).join(' · ')}
            <br />
            2024 / 2025 独立持有集 · {r.groupCount} 分组 · {r.rowCount} 行
          </p>
          <a href={externalHref(r.runId, INITIAL_EXTERNAL_FILTER, areaId)}>打开跨地区验证</a>
        </article>
      ))}
    </>
  );
}
function RunListRead({
  port,
  offset,
  areaId,
  apply,
}: {
  port: ExternalPort;
  offset: number;
  areaId: string;
  apply: (offset: number) => void;
}) {
  const read = useCallback((signal: AbortSignal) => port.runs(offset, 12, signal), [port, offset]);
  const s = useRead(read);
  return (
    <>
      <ExternalReadNotice {...s} />
      {s.status === 'READY' && s.data && (
        <>
          <ExternalRunList data={s.data} areaId={areaId} />
          <Pager
            offset={offset}
            limit={12}
            total={s.data.total}
            label="外部实验分页"
            onChange={apply}
          />
        </>
      )}
    </>
  );
}
function browserLocation() {
  return parseExternalLocation(
    typeof window === 'undefined' ? EXTERNAL_ROOT : window.location.pathname,
    typeof window === 'undefined' ? '' : window.location.search,
  );
}
export function ExternalPage({ port: injected }: { readonly port?: ExternalPort }) {
  const port = useMemo(
    () => injected ?? createExternalAdapter({ baseOrigin: apiBaseOrigin() }),
    [injected],
  );
  const [location, setLocation] = useState(browserLocation);
  useEffect(() => {
    const restore = () => setLocation(browserLocation());
    window.addEventListener('popstate', restore);
    return () => window.removeEventListener('popstate', restore);
  }, []);
  const apply = (filter: ExternalFilter) => {
    window.history.pushState(null, '', externalHref(location.id ?? '', filter, location.areaId));
    setLocation({ ...location, filter, groupId: null });
    document.getElementById('external-title')?.focus();
  };
  let valid = location.valid;
  try {
    externalQuery(location.filter);
  } catch {
    valid = false;
  }
  return (
    <div className="p01 nm-page nm-vs04-page nm-historical nm-external">
      <PlatformHeader activePage="monitoring" />
      <main id="nm-main" tabIndex={-1} className="nm-vs04-main">
        <p className="nm-vs04-breadcrumb">
          国家级 AI 消防平台 / 模型效果 / 真实历史实验 / 跨地区稳定性验证
        </p>
        <h1 id="external-title" tabIndex={-1}>
          {location.groupId ? '跨地区验证分组详情' : '跨地区稳定性验证'}
        </h1>
        <p className="nm-vs04-disclaimer">
          HISTORICAL_OFFLINE / RETROSPECTIVE_HINDCAST / RESEARCH_EVALUATION
        </p>
        <p>
          气象指标预测 ≠
          森林火灾概率。站点历史档案到达时间未知（ARCHIVE_ARRIVAL_UNKNOWN），不能证明原始实时可用或提前发布。
        </p>
        <nav className="nm-vs04-nav" aria-label="跨地区验证导航">
          <a href={historicalHref('', undefined, location.areaId)}>
            ← 原 VS05 真实历史实验（MIXED）
          </a>
          <a href={externalHref('', INITIAL_EXTERNAL_FILTER, location.areaId)}>外部验证实验列表</a>
        </nav>
        <div className="nm-management">
          {!valid ? (
            <section className="nm-management-card" role="alert">
              <h2>链接或筛选无效</h2>
              <p>runId、groupId、轨道、年度或分页不符合外部验证契约。</p>
            </section>
          ) : location.id ? (
            <RunRead
              key={location.id}
              port={port}
              id={location.id}
              groupId={location.groupId}
              filter={location.filter}
              areaId={location.areaId}
              apply={apply}
            />
          ) : (
            <RunListRead
              port={port}
              offset={location.filter.offset}
              areaId={location.areaId}
              apply={(offset) => apply({ ...location.filter, offset })}
            />
          )}
        </div>
        <footer className="nm-vs04-footer">
          P2-NO-VS06 · 原 VS03 秒级效用 NEEDS_IMPROVEMENT · 结果以 Java / SQLite 已保存实验为准
        </footer>
      </main>
    </div>
  );
}
